import { useEffect, useMemo, useRef, useState } from "react";

export default function SelectCollege({
  onChange,
  value,
  colleges,
}: {
  onChange: (value: string) => void;
  value: string | undefined;
  colleges: readonly string[] | undefined;
}) {
  const [query, setQuery] = useState(value ?? "");
  const [open, setOpen] = useState(false);
  const [extra, setExtra] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (value !== undefined) setQuery(value);
  }, [value]);

  const all = useMemo(() => [...(colleges ?? []), ...extra], [colleges, extra]);

  const filtered = useMemo(() => {
    if (!query.trim()) return all.slice(0, 50);
    const q = query.toLowerCase();
    return all.filter((c) => c.toLowerCase().includes(q)).slice(0, 50);
  }, [query, all]);

  const exactMatch = all.some((c) => c.toLowerCase() === query.trim().toLowerCase());
  const showAdd = query.trim().length > 1 && !exactMatch;

  const select = (college: string) => {
    setQuery(college);
    onChange(college);
    setOpen(false);
  };

  const addAndSelect = () => {
    const name = query.trim();
    if (!name) return;
    setExtra((prev) => [...prev, name]);
    select(name);
  };

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={query}
        placeholder="Search or type your college/school"
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
        onChange={(e) => {
          setQuery(e.target.value);
          onChange(e.target.value);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 100)}
      />
      {open && (
        <ul className="absolute left-0 right-0 z-[9999] mt-1 max-h-52 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
          {showAdd && (
            <li
              onMouseDown={(e) => { e.preventDefault(); addAndSelect(); }}
              className="cursor-pointer border-b border-gray-100 px-3 py-2.5 text-sm font-medium text-primary hover:bg-primary-50"
            >
              + Add &ldquo;{query.trim()}&rdquo;
            </li>
          )}
          {filtered.map((college) => (
            <li
              key={college}
              onMouseDown={(e) => { e.preventDefault(); select(college); }}
              className="cursor-pointer px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              {college}
            </li>
          ))}
          {filtered.length === 0 && !showAdd && (
            <li className="px-3 py-2 text-sm text-gray-400">No colleges found</li>
          )}
        </ul>
      )}
    </div>
  );
}
